<script setup>
// Config COMPLETA do node "Agente" — é a config real do agente (o canvas grava
// nos campos reais ao salvar). Inclui identidade, prompt, modelo, comportamento
// humano, Resposta e Áudio/TTS (reusa ResponseSettings/VoicePreview/VoiceSettings).
import { ref, computed, onMounted } from 'vue';
import BillingAPI from 'dashboard/api/billing';
import { useI18n } from 'vue-i18n';
import ResponseSettings from 'dashboard/components-next/agents/ResponseSettings.vue';
import ScheduleSettings from 'dashboard/components-next/agents/ScheduleSettings.vue';
import VoicePreview from 'dashboard/components-next/agents/VoicePreview.vue';
import VoiceSettings from 'dashboard/components-next/agents/VoiceSettings.vue';
import PromptGenerator from 'dashboard/components-next/agents/PromptGenerator.vue';
const props = defineProps({
  data: { type: Object, required: true },
});
const emit = defineEmits(['update', 'remove', 'close']);
const { t } = useI18n();

// Dois níveis comerciais de Lia: Full (inclusa no plano, gpt-4.1-mini) e
// 5.1 Premium (R$997/licença — gpt-5.5, casos especiais). A 5.1 só destrava
// com licença na assinatura (custom_limits lia_51_quantity, via super admin).
const LIA_MODELS = [
  {
    id: 'full',
    name: 'Lia Full',
    model: 'gpt-4.1-mini',
    provider: 'openai',
    responseBackend: 'provider_api',
    codexModel: null,
    desc: 'L11',
  },
  {
    id: 'navi-os',
    name: 'Lia Navi-OS',
    icon: '/brand-assets/navi-os-transparent.png',
    model: 'gpt-4.1-mini',
    provider: 'openai',
    responseBackend: 'codex_chatgpt',
    codexModel: 'gpt-5.6-luna',
    desc: 'NAVI',
  },
  {
    id: '5.1',
    name: 'Lia 5.1',
    model: 'gpt-5.5',
    provider: 'openai',
    desc: 'L51',
    premium: true,
  },
];
const lia51Licensed = ref(false);
const currentLia = computed(
  () =>
    LIA_MODELS.find(
      x =>
        x.model === props.data.model &&
        (x.responseBackend || 'provider_api') ===
          (props.data.responseBackend || 'provider_api')
    ) || LIA_MODELS[0]
);

const REASONING = ['off', 'adaptive', 'low', 'medium', 'high'];
// Meta de negócio: desfecho que conta como sucesso da conversa. 'tool' =
// ferramenta chamada; 'resolution' = resolvida sem humano assumir.
const META_CHOICES = [
  { value: '', kind: null },
  { value: 'agendar_reuniao', kind: 'tool', label: 'Reunião agendada' },
  { value: 'capturar_lead', kind: 'tool', label: 'Lead capturado' },
  { value: 'resolution', kind: 'resolution', label: 'Resolvida sem humano' },
];
const TONES = ['formal', 'casual', 'playful'];
const EMOJIS = ['none', 'light', 'expressive'];
const FORMATS = ['text', 'markdown'];
const DEFAULT_RESPONSE_MODE = {
  text_to: 'text',
  audio_to: 'text',
  image_to: 'text',
  video_to: 'text',
  file_to: 'text',
};

const ttsModels = computed(() => [
  { value: 'tts-1', label: t('AI_AGENTS.SETTINGS.TTS_MODELS.STANDARD') },
  { value: 'tts-1-hd', label: t('AI_AGENTS.SETTINGS.TTS_MODELS.HD') },
]);

const up = patch => emit('update', patch);
const selectLia = lia => {
  if (lia.premium && !lia51Licensed.value) return;
  up({
    model: lia.model,
    provider: lia.provider,
    responseBackend: lia.responseBackend || 'provider_api',
    codexModel: lia.codexModel || null,
  });
};
const onField = (key, event) => up({ [key]: event.target.value });
const onToggle = key => up({ [key]: !props.data[key] });
const metaValue = computed(() => {
  const m = props.data.meta;
  if (!m || !m.kind) return '';
  return m.kind === 'resolution' ? 'resolution' : m.tool || '';
});
const onMetaChange = event => {
  const choice = META_CHOICES.find(c => c.value === event.target.value);
  if (!choice || !choice.kind) {
    up({ meta: { kind: '' } });
    return;
  }
  up({
    meta: {
      kind: choice.kind,
      tool: choice.kind === 'tool' ? choice.value : '',
      label: choice.label,
    },
  });
};
// IA pra gerar/melhorar o prompt (componente já existente).
const promptGenRef = ref(null);
const applyPrompt = generated => up({ instruction: generated });
// Normalização: modelo desconhecido (legado) vira Lia Full; Lia 5.1 sem
// licença também volta pra Full (o backend impõe o mesmo).
onMounted(async () => {
  try {
    const { data } = await BillingAPI.getModules();
    lia51Licensed.value = data?.lia51_enabled === true;
  } catch {
    lia51Licensed.value = false;
  }
  const known = LIA_MODELS.find(
    x =>
      x.model === props.data.model &&
      (x.responseBackend || 'provider_api') ===
        (props.data.responseBackend || 'provider_api')
  );
  const target =
    !known || (known.premium && !lia51Licensed.value) ? LIA_MODELS[0] : known;
  if (
    props.data.model !== target.model ||
    props.data.provider !== target.provider ||
    (props.data.responseBackend || 'provider_api') !==
      (target.responseBackend || 'provider_api')
  ) {
    selectLia(target);
  }
});
</script>

<template>
  <div class="p-4">
    <div class="mb-1 flex items-center justify-between">
      <div class="flex items-center gap-2">
        <span class="i-lucide-bot text-n-slate-11" />
        <h3 class="text-sm font-semibold text-n-slate-12">
          {{ data.label || t('AI_AGENTS.FLOW.AGENT_NODE') }}
        </h3>
      </div>
      <button class="i-lucide-x text-n-slate-10" @click="emit('close')" />
    </div>
    <p class="mb-3 text-xs text-n-slate-10">
      {{ t('AI_AGENTS.FLOW.AGENT_CFG.SUBTITLE') }}
    </p>

    <!-- Nome -->
    <label class="mb-1 block text-xs text-n-slate-10">
      {{ t('AI_AGENTS.FLOW.NAME') }}
    </label>
    <input
      :value="data.label"
      class="mb-3 w-full rounded-lg border border-n-weak bg-n-solid-1 px-2.5 py-1.5 text-sm"
      @input="onField('label', $event)"
    />

    <!-- Persona: nome pelo qual a assistente se apresenta (ex.: Anna) -->
    <label class="mb-1 block text-xs text-n-slate-10">
      {{ t('AI_AGENTS.FLOW.AGENT_CFG.PERSONA') }}
    </label>
    <input
      :value="data.persona_name"
      :placeholder="t('AI_AGENTS.FLOW.AGENT_CFG.PERSONA_PH')"
      class="mb-1 w-full rounded-lg border border-n-weak bg-n-solid-1 px-2.5 py-1.5 text-sm"
      @input="onField('persona_name', $event)"
    />
    <p class="mb-3 text-xs text-n-slate-10">
      {{ t('AI_AGENTS.FLOW.AGENT_CFG.PERSONA_HINT') }}
    </p>

    <!-- Descrição -->
    <label class="mb-1 block text-xs text-n-slate-10">
      {{ t('AI_AGENTS.FORM.DESCRIPTION') }}
    </label>
    <textarea
      :value="data.description"
      rows="2"
      :placeholder="t('AI_AGENTS.FORM.DESCRIPTION_PLACEHOLDER')"
      class="mb-3 w-full rounded-lg border border-n-weak bg-n-solid-1 px-2.5 py-1.5 text-sm"
      @input="onField('description', $event)"
    />

    <!-- Prompt -->
    <div class="mb-1 flex items-center justify-between">
      <label class="block text-xs font-medium text-n-slate-11">
        {{ t('AI_AGENTS.FLOW.AGENT_CFG.INSTRUCTIONS') }}
      </label>
      <button
        class="flex items-center gap-1 text-[11px] font-medium text-n-brand hover:underline"
        @click="promptGenRef?.open()"
      >
        <span class="i-lucide-wand-2" />
        {{ t('AI_AGENTS.PROMPT_GENERATOR.GENERATE') }}
      </button>
    </div>
    <textarea
      :value="data.instruction"
      rows="12"
      :placeholder="t('AI_AGENTS.FLOW.AGENT_CFG.INSTRUCTIONS_PH')"
      class="mb-1 max-h-[60vh] min-h-[180px] w-full resize-y rounded-lg border border-n-weak bg-n-solid-1 px-2.5 py-2 font-mono text-[13px] leading-relaxed text-n-slate-12 focus:border-n-brand focus:outline-none"
      @input="onField('instruction', $event)"
    />
    <p class="mb-3 text-xs text-n-slate-10">
      {{ t('AI_AGENTS.FLOW.AGENT_CFG.INSTRUCTIONS_HINT') }}
    </p>

    <label class="mb-1 block text-xs text-n-slate-10">
      {{ t('AI_AGENTS.FLOW.AGENT_CFG.BUSINESS') }}
    </label>
    <textarea
      :value="data.businessInfo"
      rows="3"
      :placeholder="t('AI_AGENTS.FLOW.AGENT_CFG.BUSINESS_PH')"
      class="mb-1 w-full rounded-lg border border-n-weak bg-n-solid-1 px-2.5 py-1.5 text-sm"
      @input="onField('businessInfo', $event)"
    />
    <p class="mb-3 text-xs text-n-slate-10">
      {{ t('AI_AGENTS.FLOW.AGENT_CFG.BUSINESS_HINT') }}
    </p>

    <div class="mb-3 flex items-center justify-between gap-2">
      <span class="text-sm text-n-slate-12">
        {{ t('AI_AGENTS.FLOW.AGENT_CFG.INCLUDE_HISTORY') }}
      </span>
      <button
        class="relative h-5 w-9 shrink-0 rounded-full transition-colors"
        :class="data.includeHistory ? 'bg-n-brand' : 'bg-n-slate-5'"
        @click="onToggle('includeHistory')"
      >
        <span
          class="absolute top-0.5 size-4 rounded-full bg-white transition-all"
          :class="data.includeHistory ? 'left-[18px]' : 'left-0.5'"
        />
      </button>
    </div>

    <!-- Modelo (Lia): Full inclusa · 5.1 Premium com licença -->
    <div class="mb-1 flex items-center justify-between gap-2">
      <span class="text-sm text-n-slate-11">
        {{ t('AI_AGENTS.FLOW.AGENT_CFG.MODEL') }}
      </span>
      <div class="flex items-center gap-1.5">
        <button
          v-for="lia in LIA_MODELS"
          :key="lia.id"
          type="button"
          class="rounded-lg border px-2.5 py-1 text-sm font-medium flex items-center gap-1"
          :class="[
            currentLia.id === lia.id
              ? 'border-n-brand bg-n-brand/10 text-n-slate-12'
              : 'border-n-weak bg-n-solid-2 text-n-slate-11',
            lia.premium && !lia51Licensed ? 'opacity-60' : '',
          ]"
          :title="
            lia.premium && !lia51Licensed
              ? t('AI_AGENTS.FLOW.LIA.L51_LOCKED')
              : ''
          "
          @click="selectLia(lia)"
        >
          <span
            v-if="lia.premium && !lia51Licensed"
            class="i-lucide-lock w-3 h-3"
          />
          <img
            v-if="lia.icon"
            :src="lia.icon"
            alt=""
            class="size-5 object-contain"
          />
          {{ lia.name }}
        </button>
      </div>
    </div>
    <p class="mb-4 text-xs text-n-slate-10">
      {{ t(`AI_AGENTS.FLOW.LIA.${currentLia.desc}`) }}
      <template v-if="!lia51Licensed">
        {{ t('AI_AGENTS.FLOW.LIA.L51_UPSELL') }}
      </template>
    </p>

    <div class="mb-4 flex items-center justify-between gap-2">
      <span class="text-sm text-n-slate-11">
        {{ t('AI_AGENTS.FLOW.AGENT_CFG.META') }}
      </span>
      <select
        :value="metaValue"
        class="hz-select rounded-lg border border-n-weak bg-n-solid-1 py-1 text-sm"
        @change="onMetaChange"
      >
        <option
          v-for="c in META_CHOICES"
          :key="c.value || 'none'"
          :value="c.value"
        >
          {{ t(`AI_AGENTS.FLOW.AGENT_CFG.META_OPTS.${c.value || 'none'}`) }}
        </option>
      </select>
    </div>

    <div class="mb-4 flex items-center justify-between gap-2">
      <span class="text-sm text-n-slate-11">
        {{ t('AI_AGENTS.FLOW.AGENT_CFG.REASONING') }}
      </span>
      <select
        :value="data.reasoningEffort || 'off'"
        class="hz-select rounded-lg border border-n-weak bg-n-solid-1 py-1 text-sm"
        @change="onField('reasoningEffort', $event)"
      >
        <option v-for="r in REASONING" :key="r" :value="r">
          {{ t(`AI_AGENTS.FLOW.AGENT_CFG.REASONING_OPTS.${r}`) }}
        </option>
      </select>
    </div>

    <!-- Comportamento humano -->
    <div class="mb-3 rounded-lg border border-n-weak p-3">
      <p class="mb-2 text-xs font-semibold uppercase text-n-slate-10">
        {{ t('AI_AGENTS.FLOW.AGENT_CFG.HUMAN') }}
      </p>

      <div class="mb-2 flex items-center justify-between gap-2">
        <span class="text-sm text-n-slate-11">
          {{ t('AI_AGENTS.FLOW.AGENT_CFG.TONE') }}
        </span>
        <select
          :value="data.tone || 'casual'"
          class="hz-select rounded-lg border border-n-weak bg-n-solid-1 py-1 text-sm"
          @change="onField('tone', $event)"
        >
          <option v-for="o in TONES" :key="o" :value="o">
            {{ t(`AI_AGENTS.FLOW.AGENT_CFG.TONE_OPTS.${o}`) }}
          </option>
        </select>
      </div>

      <div class="mb-2 flex items-center justify-between gap-2">
        <span class="text-sm text-n-slate-11">
          {{ t('AI_AGENTS.FLOW.AGENT_CFG.EMOJIS') }}
        </span>
        <select
          :value="data.emojiStyle || 'light'"
          class="hz-select rounded-lg border border-n-weak bg-n-solid-1 py-1 text-sm"
          @change="onField('emojiStyle', $event)"
        >
          <option v-for="o in EMOJIS" :key="o" :value="o">
            {{ t(`AI_AGENTS.FLOW.AGENT_CFG.EMOJI_OPTS.${o}`) }}
          </option>
        </select>
      </div>

      <div class="mb-2 flex items-center justify-between gap-2">
        <span class="text-sm text-n-slate-11">
          {{ t('AI_AGENTS.FLOW.AGENT_CFG.SPLIT') }}
        </span>
        <button
          class="relative h-5 w-9 shrink-0 rounded-full transition-colors"
          :class="data.splitMessages ? 'bg-n-brand' : 'bg-n-slate-5'"
          @click="onToggle('splitMessages')"
        >
          <span
            class="absolute top-0.5 size-4 rounded-full bg-white transition-all"
            :class="data.splitMessages ? 'left-[18px]' : 'left-0.5'"
          />
        </button>
      </div>

      <div
        v-if="data.splitMessages"
        class="mb-2 flex items-center justify-between gap-2"
      >
        <span class="text-sm text-n-slate-11">
          {{ t('AI_AGENTS.FLOW.AGENT_CFG.LINES') }}
        </span>
        <select
          :value="data.humanizeLines || ''"
          class="hz-select rounded-lg border border-n-weak bg-n-solid-1 py-1 text-sm"
          @change="onField('humanizeLines', $event)"
        >
          <option value="">
            {{ t('AI_AGENTS.FLOW.AGENT_CFG.LINES_DEFAULT') }}
          </option>
          <option value="2">{{ t('AI_AGENTS.FLOW.AGENT_CFG.LINES_2') }}</option>
          <option value="3">{{ t('AI_AGENTS.FLOW.AGENT_CFG.LINES_3') }}</option>
          <option value="4">{{ t('AI_AGENTS.FLOW.AGENT_CFG.LINES_4') }}</option>
        </select>
      </div>

      <div class="mb-1 flex items-center justify-between gap-2">
        <span class="text-sm text-n-slate-11">
          {{ t('AI_AGENTS.FLOW.AGENT_CFG.ALWAYS_KB') }}
        </span>
        <button
          class="relative h-5 w-9 shrink-0 rounded-full transition-colors"
          :class="data.alwaysSearchKb ? 'bg-n-brand' : 'bg-n-slate-5'"
          @click="onToggle('alwaysSearchKb')"
        >
          <span
            class="absolute top-0.5 size-4 rounded-full bg-white transition-all"
            :class="data.alwaysSearchKb ? 'left-[18px]' : 'left-0.5'"
          />
        </button>
      </div>
      <p class="mb-2 text-xs text-n-slate-10">
        {{ t('AI_AGENTS.FLOW.AGENT_CFG.ALWAYS_KB_HINT') }}
      </p>

      <div class="flex items-center justify-between gap-2">
        <span class="text-sm text-n-slate-11">
          {{ t('AI_AGENTS.FLOW.AGENT_CFG.TYPING') }}
        </span>
        <button
          class="relative h-5 w-9 shrink-0 rounded-full transition-colors"
          :class="data.simulateTyping ? 'bg-n-brand' : 'bg-n-slate-5'"
          @click="onToggle('simulateTyping')"
        >
          <span
            class="absolute top-0.5 size-4 rounded-full bg-white transition-all"
            :class="data.simulateTyping ? 'left-[18px]' : 'left-0.5'"
          />
        </button>
      </div>
    </div>

    <!-- Resposta -->
    <div class="mb-3 rounded-lg border border-n-weak p-3">
      <p class="mb-3 text-xs font-semibold uppercase text-n-slate-10">
        {{ t('AI_AGENTS.SETTINGS.TABS.RESPONSE') }}
      </p>
      <ResponseSettings
        :response-delay-ms="data.response_delay_ms ?? 0"
        :audio-silence-threshold-ms="data.audio_silence_threshold_ms ?? 2000"
        :aggregate-messages="data.aggregate_messages ?? false"
        :aggregation-window-ms="data.aggregation_window_ms ?? 3000"
        :response-mode="data.response_mode ?? DEFAULT_RESPONSE_MODE"
        @update:response-delay-ms="v => up({ response_delay_ms: v })"
        @update:audio-silence-threshold-ms="
          v => up({ audio_silence_threshold_ms: v })
        "
        @update:aggregate-messages="v => up({ aggregate_messages: v })"
        @update:aggregation-window-ms="v => up({ aggregation_window_ms: v })"
        @update:response-mode="v => up({ response_mode: v })"
      />
    </div>

    <!-- Horário de atendimento da IA -->
    <div class="mb-3 rounded-lg border border-n-weak p-3">
      <p class="text-xs font-semibold uppercase text-n-slate-10">
        {{ t('AI_AGENTS.SCHEDULE.TITLE') }}
      </p>
      <p class="mb-3 text-[11px] text-n-slate-10">
        {{ t('AI_AGENTS.SCHEDULE.DESCRIPTION') }}
      </p>
      <ScheduleSettings
        :model-value="data.schedule ?? {}"
        @update:model-value="v => up({ schedule: v })"
      />
    </div>

    <!-- Áudio / TTS -->
    <div class="mb-3 rounded-lg border border-n-weak p-3">
      <div class="mb-2 flex items-center justify-between gap-2">
        <div>
          <p class="text-xs font-semibold uppercase text-n-slate-10">
            {{ t('AI_AGENTS.TTS.TITLE') }}
          </p>
          <p class="text-[11px] text-n-slate-10">
            {{ t('AI_AGENTS.TTS.DESCRIPTION') }}
          </p>
        </div>
        <button
          class="relative h-5 w-9 shrink-0 rounded-full transition-colors"
          :class="data.tts_enabled ? 'bg-n-brand' : 'bg-n-slate-5'"
          @click="onToggle('tts_enabled')"
        >
          <span
            class="absolute top-0.5 size-4 rounded-full bg-white transition-all"
            :class="data.tts_enabled ? 'left-[18px]' : 'left-0.5'"
          />
        </button>
      </div>

      <div
        v-if="data.tts_enabled"
        class="space-y-4 border-t border-n-weak pt-3"
      >
        <VoicePreview
          :model-value="data.tts_voice || 'nova'"
          :provider="data.tts_provider || 'openai'"
          @update:model-value="v => up({ tts_voice: v })"
          @update:provider="v => up({ tts_provider: v })"
        />
        <div>
          <label class="mb-2 block text-xs font-medium text-n-slate-11">
            {{ t('AI_AGENTS.TTS.MODEL') }}
          </label>
          <div class="grid grid-cols-2 gap-2">
            <button
              v-for="m in ttsModels"
              :key="m.value"
              type="button"
              class="rounded-lg border px-3 py-2 text-center text-xs transition-all"
              :class="
                (data.tts_model || 'tts-1') === m.value
                  ? 'border-n-brand bg-n-brand/10 text-n-slate-12'
                  : 'border-n-weak text-n-slate-11 hover:border-n-slate-8'
              "
              @click="up({ tts_model: m.value })"
            >
              {{ m.label }}
            </button>
          </div>
        </div>
        <VoiceSettings
          :speed="data.tts_speed ?? 1.0"
          :format="data.tts_format || 'mp3'"
          @update:speed="v => up({ tts_speed: v })"
          @update:format="v => up({ tts_format: v })"
        />
        <p class="text-[11px] text-n-slate-10">
          {{ t('AI_AGENTS.TTS.COST_INFO') }}
        </p>
      </div>
    </div>

    <!-- Transferência -->
    <label class="mb-1 block text-xs text-n-slate-10">
      {{ t('AI_AGENTS.FLOW.TRANSFER_MSG') }}
    </label>
    <textarea
      :value="data.transferMessage"
      rows="2"
      :placeholder="t('AI_AGENTS.FLOW.TRANSFER_MSG_PH')"
      class="mb-3 w-full rounded-lg border border-n-weak bg-n-solid-1 px-2.5 py-1.5 text-sm"
      @input="onField('transferMessage', $event)"
    />

    <!-- Formato da saída -->
    <div class="mb-4 flex items-center justify-between gap-2">
      <span class="text-sm text-n-slate-11">
        {{ t('AI_AGENTS.FLOW.AGENT_CFG.OUTPUT') }}
      </span>
      <select
        :value="data.outputFormat || 'text'"
        class="hz-select rounded-lg border border-n-weak bg-n-solid-1 py-1 text-sm"
        @change="onField('outputFormat', $event)"
      >
        <option v-for="f in FORMATS" :key="f" :value="f">{{ f }}</option>
      </select>
    </div>

    <button
      class="w-full rounded-lg border border-n-ruby-7 py-2 text-sm font-medium text-n-ruby-9 hover:bg-n-ruby-3"
      @click="emit('remove')"
    >
      {{ t('AI_AGENTS.FLOW.REMOVE_NODE') }}
    </button>

    <PromptGenerator
      ref="promptGenRef"
      :current-prompt="data.instruction"
      @apply="applyPrompt"
    />
  </div>
</template>

<style scoped>
/* Chevron próprio e confiável p/ os selects do inspetor (o padding pequeno
   escondia a seta global). */
.hz-select {
  appearance: none;
  -webkit-appearance: none;
  padding-inline: 0.75rem 1.75rem;
  /* a regra global de select usa content-box; forçamos padding-box p/ a seta
     ser medida a partir da borda (senão ela fica ~2rem pra dentro). */
  background-origin: padding-box;
  background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8' fill='none' stroke='rgb%28110,111,115%29' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'><path d='M2 2.5 6 6 10 2.5'/></svg>");
  background-repeat: no-repeat;
  background-position: right 0.6rem center;
  background-size: 12px 8px;
}
</style>
